/**
 * Formato de eventos: fechas.
 *
 * La regla que este fichero centraliza porque fuera de aquí se rompe sola:
 * **todo se pinta en hora de Costa Rica**, nunca en la del navegador. El backend
 * emite `starts_at` en ISO 8601 con offset (`2027-03-14T18:00:00-06:00`), que es
 * un *instante*, no una hora local. Un `DatePipe` a secas —o cualquier
 * `new Date(...).getHours()`— lo reproyecta a la zona del cliente: un usuario en
 * España vería un torneo de las 18:00 como las 02:00 del día siguiente. Todo
 * `Intl.DateTimeFormat` de este módulo fija `timeZone: STORE_TIMEZONE`, y el día
 * civil se deriva de ese formateo (`civilDayKey`), nunca de los getters de
 * `Date`, que son siempre locales al navegador.
 *
 * El importe ya NO se formatea aquí: `formatCrc` vive en
 * `shared/utils/currency.util.ts`, que es la fuente única de los dos repos.
 * `formatEventPrice` se queda porque su regla —`null` no es lo mismo que `0`— es
 * del dominio de eventos, no de la moneda.
 */

import { formatCrc } from '../../../shared/utils/currency.util';

/** Zona horaria de la tienda. Toda fecha de evento se pinta aquí, no en la del navegador. */
export const STORE_TIMEZONE = 'America/Costa_Rica';

/** Etiquetas de `type`. Vocabulario cerrado del backend (`Event::TYPES`). */
export const EVENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  torneo: 'Torneo',
  lanzamiento: 'Lanzamiento',
  precompra: 'Precompra',
  otro: 'Evento',
};

/** Un `type` fuera del vocabulario solo puede venir de una divergencia de contrato. */
export function eventTypeLabel(type: string): string {
  return EVENT_TYPE_LABELS[type] ?? EVENT_TYPE_LABELS['otro'];
}

const MONTH_NAMES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const;

/** Lunes primero: es la convención del calendario en Costa Rica y en España. */
export const WEEKDAY_INITIALS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'] as const;

// Una forma por uso, todas ancladas a la zona de la tienda. `Intl.DateTimeFormat`
// es caro de instanciar, así que se cachean; se construyen en el primer uso y no
// en la carga del módulo para que un test pueda falsificar la zona por defecto
// del entorno y comprobar que la salida no se mueve.
const FORMATS: Readonly<Record<string, Intl.DateTimeFormatOptions & { locale: string }>> = {
  // `en-CA` porque su formato nativo ya es `YYYY-MM-DD`. El locale no se muestra.
  parts: { locale: 'en-CA', year: 'numeric', month: '2-digit', day: '2-digit' },
  longDate: { locale: 'es-CR', weekday: 'long', day: 'numeric', month: 'long' },
  time: { locale: 'es-CR', hour: '2-digit', minute: '2-digit', hour12: true },
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(kind: keyof typeof FORMATS): Intl.DateTimeFormat {
  const cached = formatterCache.get(kind);
  if (cached) return cached;

  const { locale, ...options } = FORMATS[kind];
  // `timeZone` explícito SIEMPRE: sin él, `Intl` cae a la zona del navegador.
  const built = new Intl.DateTimeFormat(locale, { ...options, timeZone: STORE_TIMEZONE });
  formatterCache.set(kind, built);
  return built;
}

/**
 * @internal Solo para tests. La caché retiene formateadores ya construidos, así
 * que un spec que sustituya `Intl.DateTimeFormat` debe vaciarla para que los
 * siguientes se creen con el doble.
 */
export function resetEventFormatterCache(): void {
  formatterCache.clear();
}

function toDate(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Clave de día civil (`YYYY-MM-DD`) **en Costa Rica** para un instante ISO.
 *
 * Es la pieza sobre la que se apoya todo el calendario: agrupar por día usando
 * `date.getDate()` metería un evento de las 23:00 de CR en el día siguiente para
 * cualquier navegador al este de UTC-6. `en-CA` se usa porque su formato nativo
 * ya es `YYYY-MM-DD`; el locale no se muestra en ningún sitio.
 */
export function civilDayKey(iso: string): string | null {
  const date = toDate(iso);
  if (!date) return null;
  return formatter('parts').format(date);
}

/** `YYYY-MM` del instante, en hora de Costa Rica. */
export function civilMonthKey(iso: string): string | null {
  return civilDayKey(iso)?.slice(0, 7) ?? null;
}

/** Mes actual (`YYYY-MM`) en Costa Rica, que es el default del endpoint de calendario. */
export function currentMonthKey(now: Date = new Date()): string {
  return formatter('parts').format(now).slice(0, 7);
}

/** Día de hoy (`YYYY-MM-DD`) en Costa Rica. Marca el "hoy" del calendario. */
export function todayKey(now: Date = new Date()): string {
  return formatter('parts').format(now);
}

/** `2027-06` → `junio de 2027`. Para el encabezado del calendario. */
export function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const index = Number(month) - 1;
  const name = MONTH_NAMES[index];
  if (!name || !year) return monthKey;
  return `${name} de ${year}`;
}

/**
 * `2027-03-14` → `14 de marzo`. Rótulo de la vista día del diálogo (D25).
 *
 * Recibe un `dayKey`, que ya es el día civil **de Costa Rica** (lo calculó
 * `civilDayKey`), así que se resuelve con aritmética de cadenas y sin `Date`:
 * reinterpretarlo como instante lo reproyectaría a la zona del navegador y un
 * evento de las 23:30 caería en el día siguiente. Cadena vacía si la clave no
 * es un día reconocible.
 */
export function dayLabel(dayKey: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) return '';
  const name = MONTH_NAMES[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (!name || day < 1 || day > 31) return '';
  return `${day} de ${name}`;
}

/** Desplaza un `YYYY-MM` en meses. Aritmética pura sobre el par, sin `Date`. */
export function shiftMonth(monthKey: string, delta: number): string {
  const [yearRaw, monthRaw] = monthKey.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return monthKey;

  const zeroBased = year * 12 + (month - 1) + delta;
  const nextYear = Math.floor(zeroBased / 12);
  const nextMonth = zeroBased - nextYear * 12 + 1;
  return `${String(nextYear).padStart(4, '0')}-${String(nextMonth).padStart(2, '0')}`;
}

/** `sábado, 14 de marzo` en hora de Costa Rica. Cadena vacía si la fecha es basura. */
export function formatEventDate(iso: string): string {
  const date = toDate(iso);
  if (!date) return '';
  return formatter('longDate').format(date);
}

/** `06:00 p. m.` en hora de Costa Rica. */
export function formatEventTime(iso: string): string {
  const date = toDate(iso);
  if (!date) return '';
  return formatter('time').format(date);
}

/**
 * Rango legible de un evento. Un evento de varios días se rotula con las dos
 * fechas; uno de un solo día, con la fecha y la hora de inicio.
 */
export function formatEventRange(startsAt: string, endsAt?: string): string {
  const startDate = formatEventDate(startsAt);
  if (!startDate) return '';

  const startDay = civilDayKey(startsAt);
  const endDay = endsAt ? civilDayKey(endsAt) : null;

  if (endDay && startDay && endDay !== startDay) {
    return `${startDate} — ${formatEventDate(endsAt!)}`;
  }

  const time = formatEventTime(startsAt);
  return time ? `${startDate} · ${time}` : startDate;
}

/**
 * Hora de inicio que se repite aparte, **solo** en un evento de varios días:
 * su rango (`formatEventRange`) lleva las dos fechas y ninguna hora. En uno de
 * un día el rango ya incluye la hora, y repetirla sería ruido. Cadena vacía en
 * ese caso. La comparten tarjeta y detalle para no divergir.
 */
export function multiDayStartTime(startsAt: string, endsAt?: string): string {
  if (!endsAt) return '';
  const startDay = civilDayKey(startsAt);
  const endDay = civilDayKey(endsAt);
  return startDay && endDay && startDay !== endDay ? formatEventTime(startsAt) : '';
}

/**
 * Días civiles (en CR) que ocupa un evento, de inicio a fin, ambos incluidos.
 * Un torneo del 30 de junio al 2 de julio devuelve tres claves y por tanto se
 * pinta en sus tres casillas del calendario, no solo en la del arranque.
 *
 * El recorrido avanza en UTC sobre el mediodía del día civil: sumar 24 h a un
 * instante cualquiera puede saltarse o repetir un día bajo cambios de offset, y
 * aunque Costa Rica no aplica horario de verano, esta función no depende de ello.
 */
export function eventDayKeys(startsAt: string, endsAt?: string): string[] {
  const startKey = civilDayKey(startsAt);
  if (!startKey) return [];

  const endKey = endsAt ? civilDayKey(endsAt) : null;
  if (!endKey || endKey <= startKey) return [startKey];

  const keys: string[] = [];
  const cursor = new Date(`${startKey}T12:00:00Z`);
  const limit = new Date(`${endKey}T12:00:00Z`);

  // Tope defensivo: un `ends_at` corrupto (año 9999) no debe colgar el render.
  while (cursor <= limit && keys.length < 366) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return keys;
}

/** Precio de un evento: `null` no es lo mismo que `0` — sin precio publicado vs. gratuito. */
export function formatEventPrice(price: number | null): string {
  if (price == null || !Number.isFinite(price)) return '';
  if (price === 0) return 'Gratis';
  return formatCrc(price);
}
